import { io, type Socket } from 'socket.io-client';
import { env } from '@/lib/env';
import { getAccessToken } from '@/services/authTokenStorage';

let socket: Socket | null = null;
let socketToken: string | null = null;
let socketReady = false;
let retainCount = 0;

export function createSocket(url: string = env.wsUrl): Socket {
  const created = io(url, {
    autoConnect: false,
    transports: ['websocket'],
  });
  created.on('realtime.connected', () => {
    if (socket === created) socketReady = true;
  });
  created.on('disconnect', () => {
    if (socket === created) socketReady = false;
  });
  return created;
}

export function getSocket(): Socket {
  if (!socket) {
    socket = createSocket();
  }
  return socket;
}

/**
 * Connect the shared socket with the current access token. A changed token
 * always forces a new Socket.IO handshake so a previous tenant room cannot
 * survive an auth-session change.
 */
export function connectSocket(): Socket {
  const current = getSocket();
  const token = getAccessToken();
  const tokenChanged = token !== socketToken;

  current.auth = { token };

  if (tokenChanged) {
    socketReady = false;
    if (current.connected || current.active) {
      current.disconnect();
    }
  }

  socketToken = token;
  if (!current.connected) {
    current.connect();
  }

  return current;
}

export function retainSocket(): Socket {
  retainCount += 1;
  return connectSocket();
}

/** True only after the gateway has authenticated the transport session. */
export function isSocketReady(): boolean {
  return Boolean(socket?.connected && socketReady);
}

export function releaseSocket(): void {
  if (retainCount > 0) {
    retainCount -= 1;
  }

  if (retainCount === 0) {
    resetSocket();
  }
}

/** Immediately tear down every reference, including explicit sign-out. */
export function disconnectSocket(): void {
  retainCount = 0;
  resetSocket();
}

function resetSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  socketToken = null;
  socketReady = false;
}
